import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
const _excluded = ["button", "children", "className", "href", "isSocial", "noStyle", "onClick", "onMouseDown", "rel", "target", "disableDefaultEventTracking", "disableDefaultFunnelTracking", "trackId", "testId", "trackEventData", "trackProductId"];
function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
import * as React from 'react';
import classnames from 'classnames';
import { onlyText } from 'react-children-utilities';
import { AnalyticsContext, AnalyticsLocationConsumer, trackEvent, getInternalTrafficSource } from 'latitude-analytics';
import { BUTTON_STYLE } from '@latitude/core/utils/constants';
import { StyledLink } from './Link.styled';
const isFile = href => /\.(exe|zip|wav|mp3|mov|mpg|avi|wmv|pdf|doc|docx|xls|xlsx|ppt|pptx)$/.test(href);
const isAbsoluteUrl = href => new RegExp('^(?:[a-z]+:)?//', 'i').test(href);
const isTel = href => href.startsWith('tel:');
const isEmail = href => href.startsWith('mailto:');
const getLabel = children => children && children.type && children.type === 'img' ? "image" : onlyText(children).replace(/\[object Object\]/g, '');
const isExternal = href => {
  if (!isAbsoluteUrl(href)) {
    return false;
  }
  const internalUrls = ['latitudepay.com', 'latitudepay.com.au', 'gemfinance.co.nz', 'gemcreditline.co.nz', 'latitudefinancial.com', 'latitudefinancial.com.au', 'latitude.veda.com.au', 'gomastercard.com.au', 'gemvisa.com.au', '28degreescard.com.au', 'buyersedge.com.au', 'interestfree.com.au', 'umiloans.com.au', 'genoapay.com', 'mypersonalloanapp.com', 'lfscnp.com', 'lfs.corefinance.io', 'latitudefs.zendesk.com', 'gemfinance.zendesk.com'];
  const url = new URL(href, typeof window !== 'undefined' ? window.location.href : 'https://www.latitudefinancial.com.au');
  return !internalUrls.reduce((previous, current) => previous || url.hostname.indexOf(current) !== -1, false);
};

/**
 * URLs which are used as part of our application funnels.
 * Each is accounted for to provide accurate tracking event values
 * across dev, qa and prod environments
 */

const FUNNEL_URL = {
  CC: {
    DEV: 'https://cx-dev04.lfs.corefinance.io/apply',
    QA: 'https://cx-test04.lfs.corefinance.io/apply',
    PROD: 'https://cardapp.latitudefinancial.com/apply'
  },
  ML: {
    DEV: 'https://motorapp-dev2.001.development.odds-np.lfscnp.com',
    QA: 'https://motorapp-test2.001.development.odds-np.lfscnp.com',
    PROD: 'https://motorapp.latitudefinancial.com.au'
  },
  PL: {
    // The FUNNEL_URL.PL values are defined both here and in https://github.com/LatitudeFinancial/latitudefinancial-com-au/blob/ad04b2e53b995d626c246f9d1042af30c3d40f33/src/utils/constants.js#L462
    DEV: 'https://origqa-gecapitalau.cs5.force.com/latitude',
    QA: 'https://preprod-gecapitalau.cs31.force.com/latitude',
    PROD: 'https://lfs-apply.mypersonalloanapp.com',
    LANDING_PAGE_BETTER_START: '/loans/apply-better-start/',
    LANDING_PAGE: '/loans/apply/'
  }
};
const SOFT_QUOTE_URLS = {
  PROD: /^https:\/\/quote\.latitudefinancial\.com\.au\//,
  NON_PROD: /^https:\/\/([0-9a-zA-Z\-]+)\.staging\.quote-latitudefinancial\.digitalweb-np\.lfscnp\.com\//,
  QA_LOANS: /^https:\/\/fullcopy3-sympleloans\.cs57\.force\.com\/latitudeUnsecured\/s\/quote/,
  // This is the value for SYMPLE QA
  PROD_LOANS: /^https:\/\/loans\.portal\.latitudefinancial\.com\.au\/s\/quote/ // This is the value for SYMPLE PROD
};
const SOFT_QUOTE_AU_RETRIEVE_URL = '/au/retrieve';
const isSoftQuote = href => {
  for (const env in SOFT_QUOTE_URLS) {
    if (SOFT_QUOTE_URLS[env].test(href)) return true;
  }
  return false;
};

/**
 * 
 * TODO: When a new key value pair is added to the FUNNEL_URL.CC object defined above, this function also needs to be updated.
 * An alternative was to see if href is contained in FUNNEL_URL is:
 * 
 * const isApplicationFunnel = (href: string) => {
 *   for (const product in FUNNEL_URL) {
 *     for (const env in FUNNEL_URL[product]) {
 *       if (href.startsWith(FUNNEL_URL[product][env])) return true;
 *     }
 *   }
 *   if (href.startsWith('https://apply.gemvisa.co.nz/eapps/faces/LongAppRetail/personal.xhtml')) return true;
 *   return false;
 * }
 * 
 * This would make the code more reobust as a typo in the code below causing a mismatch between (say) *.*.PR0D and *.*.PROD 
 * would go undetected as .startsWith(undefined) wound not return truthy.
 */

const isFunnel = href => href.startsWith(FUNNEL_URL.CC.DEV) || href.startsWith(FUNNEL_URL.CC.QA) || href.startsWith(FUNNEL_URL.CC.PROD) || href.startsWith(FUNNEL_URL.ML.DEV) || href.startsWith(FUNNEL_URL.ML.QA) || href.startsWith(FUNNEL_URL.ML.PROD) || href.startsWith(FUNNEL_URL.PL.DEV) || href.startsWith(FUNNEL_URL.PL.QA) || href.startsWith(FUNNEL_URL.PL.PROD) || href.endsWith(FUNNEL_URL.PL.LANDING_PAGE) || href.endsWith(FUNNEL_URL.PL.LANDING_PAGE_BETTER_START) || href.startsWith('https://apply.gemvisa.co.nz/eapps/faces/LongAppRetail/personal.xhtml');
const OSC_URL = ['https://online.gemvisa.com.au', 'https://gomastercard-online.gomastercard.com.au', 'https://gobusiness-online.gomastercard.com.au', 'https://28degrees-online.latitudefinancial.com.au', 'https://latitudeinfinity-online.latitudefinancial.com.au', 'https://latitudemastercard-online.latitudefinancial.com.au', 'https://eco-online.latitudefinancial.com.au', 'https://lowrate-online.latitudefinancial.com.au', 'https://online.interestfree.com.au', 'https://online.buyersedge.com.au', 'https://online.gemvisa.co.nz', 'https://online.gemcreditline.co.nz', 'https://online.onecardvisa.co.nz'];
const isOSC = href => OSC_URL.filter(oscUrl => href.startsWith(oscUrl)).length;
const getEventData = (href, children, location, trackEventData, button, isSocial) => {
  let category = 'text-link';
  let action = 'internal-link';
  if (button) {
    category = 'button-link';
  }
  if (isSocial) {
    action = 'social-link';
  }
  if (typeof href === 'string') {
    // test to be tollerent of null hrefs which can occur in modal opening links like <Link href={null} onClick={() => setIsOpen(true)}> (eg in apple-creditline.js), even though href is required by typescript.
    if (isExternal(href)) {
      action = 'external-link';
    }
    if (isTel(href)) {
      action = 'phone-link';
      category = 'contact-us';
    }
    if (isEmail(href)) {
      action = 'email-link';
      category = 'contact-us';
    }
    if (isFunnel(href)) {
      action = 'application-link';
      category = 'cta';
      if (href.endsWith(FUNNEL_URL.PL.LANDING_PAGE) || href.endsWith(FUNNEL_URL.PL.LANDING_PAGE_BETTER_START)) {
        action = 'discovery-link';
      }
    }
    if (isOSC(href)) {
      action = 'service-centre-link';
    }
    if (isSoftQuote(href)) {
      category = 'cta';
      if (href.endsWith(SOFT_QUOTE_AU_RETRIEVE_URL)) {
        action = 'quote-retrieval-link';
      } else {
        action = 'quote-link';
      }
    }
  }
  return _objectSpread({
    category,
    action,
    label: getLabel(children, trackEventData),
    location
  }, trackEventData);
};
export const Link = _ref => {
  let {
      button,
      children,
      className,
      href,
      isSocial = false,
      noStyle = false,
      onClick,
      onMouseDown,
      rel,
      target,
      disableDefaultEventTracking = false,
      disableDefaultFunnelTracking = false,
      trackId,
      testId,
      trackEventData = {},
      trackProductId
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  const [analytics] = React.useContext(AnalyticsContext);
  const [tealiumDebugMode, setTealiumDebugMode] = React.useState(false);
  React.useEffect(() => {
    const isBrowser = typeof window !== "undefined";
    if (isBrowser) {
      setTealiumDebugMode(localStorage.getItem('tealiumDebugMode') || false);
    }
  }, []);
  const isUrlExternal = href && href.startsWith('http');
  const getRel = () => !rel && isUrlExternal && target === '_blank' ? 'noopener noreferrer' : rel;
  const getTarget = () => {
    if (!target && isUrlExternal && !href.endsWith(FUNNEL_URL.PL.LANDING_PAGE) && !href.endsWith(FUNNEL_URL.PL.LANDING_PAGE_BETTER_START)) {
      return '_blank';
    }
    return target;
  };
  const handleOnClick = (event, location) => {
    if (disableDefaultEventTracking !== true) {
      if (!isFile(href)) {
        // set specific product id
        if (trackProductId && analytics.pageData) {
          analytics.pageData.product_productid = trackProductId;
        }
        trackEvent(analytics, getEventData(href, children, location, trackEventData, button, isSocial));
      }
    }
    if (onClick) {
      onClick(event);
    }
  };
  const getTealiumDebugData = location => {
    const eventData = getEventData(href, children, location, trackEventData, button, isSocial);
    let output;
    if (analytics.siteData && analytics.siteData.page) {
      output = _objectSpread(_objectSpread(_objectSpread({}, analytics.siteData.page), analytics.pageData), eventData);
    } else {
      output = _objectSpread(_objectSpread({}, analytics.pageData), eventData);
    }
    return JSON.stringify(output, null, 2);
  };
  return /*#__PURE__*/React.createElement(AnalyticsLocationConsumer, null, location => {
    let to = href;
    const tealiumDebugData = getTealiumDebugData(location);
    if (isUrlExternal && (!isExternal(href) || isFunnel(href)) && analytics.pageData && !isFile(href) && analytics.siteData && analytics.siteData.page) {
      const eventData = getEventData(href, children, location, trackEventData, button, isSocial);
      const url = new URL(href, typeof window !== 'undefined' ? window.location.href : 'https://www.latitudefinancial.com.au');
      const parameters = new URLSearchParams(url.search);
      const pageData = Object.keys(analytics.pageData).reduce((prev, current) => _objectSpread(_objectSpread({}, prev), {}, {
        [current.substring(5)]: analytics.pageData[current]
      }), {});

      /**
       * Note: Current PL funnels need to be disabled due to Salesforce/Tealium integration issues
       * Can remove conditional once this has been resolved.
       */
      // Disabling funnel tracking for gemid2 on non-prod environment
      // if (href.startsWith(FUNNEL_URL.PL.PROD) !== true) {
      /**
       * Certain use cases require the gemid2 funnel tracking params to be disabled.
       */
      if (disableDefaultFunnelTracking !== true) {
        parameters.append('gemid2', getInternalTrafficSource({
          pageData: {
            page: pageData
          },
          eventData: {
            event: eventData
          },
          siteData: analytics.siteData
        }));
      }
      // }

      url.search = parameters.toString();
      to = url.href;
    }
    return /*#__PURE__*/React.createElement(StyledLink, Object.assign({
      className: classnames(trackId && 'trackcustomanalytics', className),
      href: to,
      rel: getRel(),
      target: getTarget(),
      button: button,
      "data-trackid": trackId,
      noStyle: noStyle,
      onClick: event => handleOnClick(event, location),
      onMouseDown: onMouseDown,
      "data-test-id": testId,
      title: tealiumDebugMode ? tealiumDebugData : null,
      tealiumDebugMode: tealiumDebugMode
    }, rest), children);
  });
};
export default Link;